// Mod routing — connects the single global LFO to one destination AudioParam.
//
// Web Audio sums a-rate connections onto a param's intrinsic value, so the LFO
// rides *on top* of the base value set by `setParam` without ever overwriting
// it. The destination's "scale" converts the LFO's normalized [-1,1] swing into
// the param's native units (Hz, cents, gain, ...).
//
// Destinations can resolve to several AudioParams (e.g. the same param across 6
// voices), so a route fans the scaled signal out to all of them.

import { isParamId, type ParamId } from './params';
import { NO_DEST } from './params';

/** Resolve a destination id to the live AudioParams that back it (per voice). */
export type ResolveTargets = (id: ParamId) => AudioParam[];
/** Native half-swing (depth = 1) for a destination, in the param's own units. */
export type ScaleFor = (id: ParamId) => number;

export class ModRouter {
  private readonly source: AudioNode;
  private readonly ctx: BaseAudioContext;
  private readonly resolve: ResolveTargets;
  private readonly scaleFor: ScaleFor;

  private destId: ParamId | null = null;
  private scaleGain: GainNode | null = null;

  constructor(
    ctx: BaseAudioContext,
    source: AudioNode,
    resolve: ResolveTargets,
    scaleFor: ScaleFor,
  ) {
    this.ctx = ctx;
    this.source = source;
    this.resolve = resolve;
    this.scaleFor = scaleFor;
  }

  get destination(): ParamId | null {
    return this.destId;
  }

  /** Route the LFO to `destId` (or `'none'`/invalid to clear). */
  setRoute(destId: string): void {
    this.clearRoute();
    if (destId === NO_DEST || !isParamId(destId)) return;

    const targets = this.resolve(destId);
    if (targets.length === 0) {
      // Nothing live to drive yet (e.g. param appears in a later phase); still
      // record intent so a later refresh() can connect it.
      this.destId = destId;
      return;
    }

    const scale = this.ctx.createGain();
    scale.gain.value = this.scaleFor(destId);
    this.source.connect(scale);
    for (const target of targets) scale.connect(target);

    this.scaleGain = scale;
    this.destId = destId;
  }

  clearRoute(): void {
    if (this.scaleGain) {
      this.source.disconnect(this.scaleGain);
      this.scaleGain.disconnect();
      this.scaleGain = null;
    }
    this.destId = null;
  }

  /** Re-resolve the current destination (call when the voice set changes). */
  refresh(): void {
    const id = this.destId;
    if (id === null) return;
    this.setRoute(id);
  }
}
